import * as THREE from 'three';
import { offscreen } from './platform.js';
import { BOAT_MOOR, seatAvatarInBoat } from './boat.js';
import { resetAvatarSeatedJoints } from './avatarMotion.js';
import { HOME, ISLANDS, PORTS, autopilotInput, landingPoint, normalizeVoyage, planSeaRoute, stepHelm, harvestIsland } from './voyage.js';
import { createIslandArt, animateIslandArt, islandPropCollider, islandWalkPoint, islandTerrainHeight as terrainHeight } from './voyageArt.js';
import { voyageSupplies } from './playerGuidance.js';
import { islandNotes } from './exploration.js';
import { interactQuestSite, questSummary } from './beaconQuest.js';
import { createQuestLandmarks } from './beaconQuestVisual.js';
import { voyageLayout, canvasButtonAt } from './voyageLayout.js';
import { resizeTextureCanvas } from './canvasTexture.js';
import { blocked, clearSegment, planWalkRoute } from './navigation.js';
import { CHARACTER_RADIUS, CHARACTER_HEIGHT, collisionObstacles, resolveCharacterStep, createVault, stepVault } from './characterCollision.js';


// Canvas HUD keeps mouse/touch and the WeChat build on the same interaction model.
export function createVoyageScene({ scene, hudScene, state, interactive, getAvatar, getBoat, flash, save,
  boardFromHome, onHome, closeHud, isBlocked, isHudOpen, useSupply, palmSource, artAssets, getScreen, getHudBounds, surfUniforms, onDiscovery = () => {} }) {
  state.voyage = normalizeVoyage(state.voyage);
  const v = state.voyage;
  const remoteGround = [], islandArt = [], questArt = [], nodes = new Map(), keys = new Set();
  const islandColliders = new Map(), islandObstacles = new Map(), emptyColliders = [];
  let walkRoute = [], activeVault = null, vaultFrame = null;
  let mapOpen = false, selectedId = v.trackedId || questSummary(state).nextIslandId || 'beacon', route = [], destinationId = null;
  let pendingStartDestination = null;
  let suppliesOpen = false;
  let walkTarget = null, pendingGather = null, pendingDeparture, hold = null, age = 0, paintAt = -1, collisionAt = -10;
  let manualInputActive = false, manualMoving = false;
  const chartCanvas = offscreen(600, 710), helmCanvas = offscreen(720, 330);
  const chartTexture = new THREE.CanvasTexture(chartCanvas), helmTexture = new THREE.CanvasTexture(helmCanvas);
  chartTexture.colorSpace = THREE.SRGBColorSpace; helmTexture.colorSpace = THREE.SRGBColorSpace;
  const chart = new THREE.Sprite(new THREE.SpriteMaterial({ map: chartTexture, depthTest: false }));
  const helm = new THREE.Sprite(new THREE.SpriteMaterial({ map: helmTexture, depthTest: false }));
  chart.center.set(0, 1); helm.center.set(0, 1); hudScene.add(chart, helm);
  let chartRect, helmRect, chartButtons = [], helmButtons = [];
  const landFocus = { x: 0, z: 0 }, overviewFocus = { x: 0, z: 0, radius: 14 };
  const label = (text, parent, x, y, z, width = 2.5) => {
    const c = offscreen(384, 72), ctx = c.getContext('2d');
    ctx.fillStyle = 'rgba(18,61,64,.9)'; ctx.fillRect(0, 0, 384, 72); ctx.strokeStyle = '#ead3a3'; ctx.lineWidth = 3; ctx.strokeRect(2, 2, 380, 68);
    ctx.fillStyle = '#fff2cc'; ctx.font = '700 32px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(text, 192, 48);
    const texture = new THREE.CanvasTexture(c); texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture }));
    sprite.position.set(x, y, z); sprite.scale.set(width, width * .1875, 1); parent.add(sprite); return sprite;
  };
  for (const island of ISLANDS) {
    const art = createIslandArt({ island, assets: { ...artAssets, palm: palmSource }, label, surfUniforms });
    islandArt.push(art); scene.add(art.root, art.buoy); remoteGround.push(art.terrain);
    const { resource, buoy, drift } = art;
    buoy.userData.action = { type: 'voyage-port', islandId: island.id }; interactive.push(buoy);
    resource.userData.action = { type: 'voyage-gather', islandId: island.id, node: 'main' };
    interactive.push(resource); nodes.set(`${island.id}-main`, resource);
    drift.userData.action = { type: 'voyage-gather', islandId: island.id, node: 'wood' }; interactive.push(drift); nodes.set(`${island.id}-wood`, drift);
    const landmarks = createQuestLandmarks({ island, root: art.root });
    questArt.push(landmarks);
    for (const node of landmarks.nodes) {
      interactive.push(node); nodes.set(node.userData.action.siteId, node);
      node.traverse(object => {
        if (!object.isMesh || object.isInstancedMesh || !['CylinderGeometry', 'BoxGeometry', 'RoundedBoxGeometry', 'SphereGeometry'].includes(object.geometry.type)) return;
        const collider = islandPropCollider(object, `${island.id}-quest-${art.colliders.length}`, { kind: 'quest-prop', shape: object.geometry.type === 'CylinderGeometry' ? 'circle' : 'oriented-box', low: false });
        if (collider) art.colliders.push(collider);
      });
    }
    islandColliders.set(island.id, art.colliders);
    const groundProps = art.colliders.filter(c => {
      const x = c.x ?? (c.x0 + c.x1) / 2, z = c.z ?? (c.z0 + c.z1) / 2, ground = terrainHeight(island, x, z);
      return c.top > ground + 1e-7 && c.bottom < ground + CHARACTER_HEIGHT;
    });
    const obstacles = collisionObstacles(groundProps).map(c => c.type === 'box'
      ? { ...c, x0: c.x0 - island.x, x1: c.x1 - island.x, z0: c.z0 - island.z, z1: c.z1 - island.z }
      : { ...c, x: c.x - island.x, z: c.z - island.z });
    // The circular planner is kept island-local. Conservative shore cells
    // prevent a detour around a prop from cutting through a narrow cove.
    const limit = island.radius - 1.5, extent = Math.ceil(limit / .4);
    for (let j = -extent; j <= extent; j++) for (let i = -extent; i <= extent; i++) {
      const x = i * .4, z = j * .4;
      if (Math.hypot(x, z) > limit || shoreSafe(island, island.x + x, island.z + z)) continue;
      obstacles.push({ id: `shore-${i}-${j}`, type: 'box', x0: x - .20, x1: x + .20, z0: z - .20, z1: z + .20 });
    }
    islandObstacles.set(island.id, obstacles);
  }
  function shoreSafe(island, x, z) {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
    const shore = islandWalkPoint(island, { x, z });
    return Math.hypot(shore.x - x, shore.z - z) < 1e-9 && terrainHeight(island, x, z) > .12;
  }
  function canStand(island, x, z) {
    return shoreSafe(island, x, z) && !blocked(x - island.x, z - island.z, islandObstacles.get(island.id), island.radius - 1.5);
  }
  function movementBand(island, from, to) {
    const first = terrainHeight(island, from.x, from.z), last = terrainHeight(island, to.x, to.z);
    return { feetY: Math.min(first, last), height: CHARACTER_HEIGHT + Math.abs(last - first) };
  }
  function shoreStep(island, from, desired) {
    let previous = 0;
    for (let i = 1; i <= 8; i++) {
      const t = i / 8, p = { x: from.x + (desired.x - from.x) * t, z: from.z + (desired.z - from.z) * t };
      if (shoreSafe(island, p.x, p.z)) { previous = t; continue; }
      let low = previous, high = t;
      for (let pass = 0; pass < 20; pass++) {
        const middle = (low + high) / 2, x = from.x + (desired.x - from.x) * middle, z = from.z + (desired.z - from.z) * middle;
        if (shoreSafe(island, x, z)) low = middle; else high = middle;
      }
      return { x: from.x + (desired.x - from.x) * low, z: from.z + (desired.z - from.z) * low };
    }
    return desired;
  }
  function resolveIslandStep(island, from, target) {
    const colliders = islandColliders.get(island.id), band = movementBand(island, from, target);
    let desired = target, limited = false;
    const startAtBand = options => resolveCharacterStep(from, from, colliders, options);
    const actualStart = startAtBand(movementBand(island, from, from));
    if (Math.hypot(actualStart.x - from.x, actualStart.z - from.z) > 1e-6) return { ...from, blocked: true, hits: actualStart.hits };
    const lowerBand = startAtBand(band);
    if (Math.hypot(lowerBand.x - from.x, lowerBand.z - from.z) > 1e-6) {
      // Descending terrain can introduce a prop that was below the old feet.
      // Stop before that vertical overlap instead of pushing the actor sideways.
      let low = 0, high = 1;
      for (let pass = 0; pass < 20; pass++) {
        const t = (low + high) / 2, p = { x: from.x + (target.x - from.x) * t, z: from.z + (target.z - from.z) * t };
        const atStart = startAtBand(movementBand(island, from, p));
        if (Math.hypot(atStart.x - from.x, atStart.z - from.z) < 1e-6) low = t; else high = t;
      }
      desired = { x: from.x + (target.x - from.x) * low, z: from.z + (target.z - from.z) * low }; limited = true;
    }
    const result = resolveCharacterStep(from, desired, colliders, movementBand(island, from, desired));
    if (Math.hypot(result.x - from.x, result.z - from.z) > Math.hypot(target.x - from.x, target.z - from.z) + 1e-5) return { ...from, blocked: true, hits: result.hits };
    return { ...result, blocked: result.blocked || limited };
  }
  function clearGroundSegment(island, from, to) {
    if (!clearSegment({ x: from.x - island.x, z: from.z - island.z }, { x: to.x - island.x, z: to.z - island.z }, islandObstacles.get(island.id), island.radius - 1.5)) return false;
    const count = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.z - from.z) / .12));
    for (let i = 0; i <= count; i++) if (!shoreSafe(island, from.x + (to.x - from.x) * i / count, from.z + (to.z - from.z) * i / count)) return false;
    return true;
  }
  function restoreWalkPoint(island, position) {
    const start = islandWalkPoint(island, position);
    if (canStand(island, start.x, start.z)) return start;
    const colliders = islandColliders.get(island.id), ground = terrainHeight(island, start.x, start.z);
    const overlapIds = collisionObstacles(colliders).filter(c => blocked(start.x, start.z, [c], Infinity)).map(c => c.id);
    // A legacy position inside a newly physical prop needs a nearby exit.
    // Other tall walls remain solid; a low rim may be cleared during recovery.
    const ignored = colliders.filter(c => overlapIds.includes(c.id) || c.low !== false && c.top - ground <= .65).map(c => c.id);
    const clearRecovery = p => {
      if (!canStand(island, p.x, p.z) || Math.hypot(p.x - start.x, p.z - start.z) > 3) return false;
      const resolved = resolveCharacterStep(start, p, colliders, { feetY: ground, ignoreIds: ignored });
      if (Math.hypot(resolved.x - p.x, resolved.z - p.z) > 1e-5) return false;
      const count = Math.max(1, Math.ceil(Math.hypot(p.x - start.x, p.z - start.z) / .12));
      for (let i = 0; i <= count; i++) if (!shoreSafe(island, start.x + (p.x - start.x) * i / count, start.z + (p.z - start.z) * i / count)) return false;
      return true;
    };
    const separation = resolveCharacterStep(start, start, colliders, { feetY: ground });
    if (clearRecovery(separation)) return { x: separation.x, z: separation.z };
    for (let radius = .12; radius <= 3; radius += .12) for (let i = 0; i < 64; i++) {
      const angle = i * Math.PI * 2 / 64, p = { x: start.x + Math.cos(angle) * radius, z: start.z + Math.sin(angle) * radius };
      if (clearRecovery(p)) return p;
    }
    return null;
  }
  function queueWalk(target, action = null, departure) {
    if (activeVault) { flash('翻越完成后再选择去向'); return false; }
    const island = ISLANDS.find(i => i.id === v.islandId), avatar = getAvatar();
    if (!island || !avatar) return false;
    const start = { x: avatar.position.x, z: avatar.position.z }, destination = islandWalkPoint(island, target);
    const candidates = [];
    if (!action && canStand(island, destination.x, destination.z)) candidates.push(destination);
    const baseAngle = Math.atan2(start.z - target.z, start.x - target.x);
    const radii = departure !== undefined ? [0, .10, .20] : action ? action.type === 'voyage-quest' ? [1.25, 1.55, 1.8, 1.97] : [1.75, 1.9, 1.97, 1.4, 1.1] : [.4, .8, 1.2, 1.6, 1.95];
    for (const radius of radii) for (let i = 0; i < 40; i++) {
      const angle = baseAngle + i * Math.PI * 2 / 40, p = { x: target.x + Math.cos(angle) * radius, z: target.z + Math.sin(angle) * radius };
      if (canStand(island, p.x, p.z)) candidates.push(p);
    }
    candidates.sort((a, b) => a === destination ? -1 : b === destination ? 1 : Math.hypot(a.x - start.x, a.z - start.z) - Math.hypot(b.x - start.x, b.z - start.z));
    for (const goal of candidates) {
      const path = planWalkRoute({ x: start.x - island.x, z: start.z - island.z }, { x: goal.x - island.x, z: goal.z - island.z }, islandObstacles.get(island.id), island.radius - 1.5)
        .map(p => ({ x: island.x + p.x, z: island.z + p.z }));
      if (!path.length || Math.hypot(path.at(-1).x - goal.x, path.at(-1).z - goal.z) > .08) continue;
      let previous = start;
      if (!path.every(p => { const safe = clearGroundSegment(island, previous, p); previous = p; return safe; })) continue;
      stopManualMove(); walkRoute = path; walkTarget = goal; pendingGather = action; pendingDeparture = departure;
      return true;
    }
    stopWalk(); flash('这里被实物挡住了，请换一处落脚点'); return false;
  }
  function tryVault(dx, dz) {
    const island = ISLANDS.find(i => i.id === v.islandId), avatar = getAvatar();
    if (v.mode !== 'ashore' || activeVault || !island || !avatar || mapOpen || isHudOpen() || isBlocked()) return false;
    const direction = { dx: Number.isFinite(dx) ? dx : Math.sin(avatar.rotation.y), dz: Number.isFinite(dz) ? dz : Math.cos(avatar.rotation.y) };
    const plan = createVault({ x: avatar.position.x, z: avatar.position.z }, direction, islandColliders.get(island.id), {
      feetY: terrainHeight(island, avatar.position.x, avatar.position.z), groundHeightAt: (x, z) => terrainHeight(island, x, z), canStandAt: (x, z) => shoreSafe(island, x, z),
    });
    if (!plan) return false;
    stopWalk(); manualInputActive = true; activeVault = plan; vaultFrame = null;
    avatar.rotation.y = Math.atan2(direction.dx, direction.dz);
    return true;
  }
  function stopManualMove() { manualInputActive = false; manualMoving = false; }
  function stopWalk() { walkTarget = null; walkRoute = []; pendingGather = null; pendingDeparture = undefined; stopManualMove(); }
  function manualMove(dx, dz, seconds) {
    const avatar = getAvatar(), island = ISLANDS.find(i => i.id === v.islandId);
    const length = Math.hypot(dx, dz), dt = Math.min(.1, Math.max(0, Number.isFinite(seconds) ? seconds : 0));
    if (v.mode !== 'ashore' || !avatar || !island || mapOpen || isHudOpen() || isBlocked()
      || !Number.isFinite(length) || length < .001 || !dt) { stopManualMove(); return false; }
    if (activeVault) return true;
    // A held input takes over once. Stopping it must never erase a new tap walk.
    if (!manualInputActive) { stopWalk(); route = []; destinationId = null; manualInputActive = true; }
    const scale = dt * 3 / Math.max(1, length);
    const start = { x: avatar.position.x, z: avatar.position.z };
    const desired = shoreStep(island, start, { x: start.x + dx * scale, z: start.z + dz * scale });
    const point = resolveIslandStep(island, start, desired);
    if (point.blocked && tryVault(dx, dz)) return true;
    if (!shoreSafe(island, point.x, point.z)) { manualMoving = false; return true; }
    manualMoving = Math.hypot(point.x - avatar.position.x, point.z - avatar.position.z) > 1e-6;
    resetAvatarSeatedJoints(avatar);
    avatar.position.set(point.x, terrainHeight(island, point.x, point.z) + .02, point.z);
    avatar.rotation.set(0, Math.atan2(dx, dz), 0);
    v.walkX = point.x; v.walkZ = point.z;
    return true;
  }
  function openMap() {
    if (!mapOpen && state.beaconQuest.seals.includes(selectedId)) selectedId = questSummary(state).nextIslandId || selectedId;
    mapOpen = !mapOpen; if (mapOpen) stopManualMove(); closeHud();
  }
  function board() {
    if (state.boatDurability <= 0 || state.boatTier === 'wreck') { flash('船只已损坏，先在码头修复'); return false; }
    v.mode = 'sailing'; v.islandId = null; v.speed = 0; state.boatMoored = false;
    if (getAvatar()) getAvatar().visible = true;
    stopWalk(); closeHud(); save(state); paintAt = -1;
    flash('已登船 · WASD 驾驶，或点海面航行；海图可规划航线');
    if (pendingStartDestination) {
      const destination = pendingStartDestination; pendingStartDestination = null;
      setDestination(destination);
    }
    return true;
  }
  function setDestination(id) {
    if (v.mode !== 'sailing') { flash('先在码头登船，再选择航线'); return; }
    const island = PORTS.find(i => i.id === id);
    const planned = planSeaRoute(v, landingPoint(island));
    if (!planned.length) { flash('航路受阻，请手动驶离岸边再试'); return; }
    route = planned; destinationId = id; mapOpen = false; closeHud(); paintAt = -1;
    v.trackedId = id; save(state);
    flash(`航向 ${islandNotes(state, island).name} · 自动避开陆地，WASD 可接管`);
  }
  function walkBackToBoat(nextDestination = null) {
    stopManualMove();
    const land = landingPoint(ISLANDS.find(i => i.id === v.islandId), true);
    mapOpen = false; closeHud();
    if (queueWalk(land, null, nextDestination)) flash('正在走回登陆点 · 到达后登船');
  }
  function dock() {
    if (v.mode === 'ashore') { walkBackToBoat(); return; }
    if (v.mode !== 'sailing') { boardFromHome(); return; }
    const port = PORTS.find(i => Math.hypot(v.x - landingPoint(i).x, v.z - landingPoint(i).z) < 4.5);
    if (!port) { flash('靠近岛屿黄色浮标（4.5 米内），再靠岸下船'); return; }
    if (v.speed > 1.5) { flash('先松开油门或按 S 减速，再安全靠岸'); return; }
    route = []; destinationId = null; v.speed = 0;
    const position = landingPoint(port); v.x = position.x; v.z = position.z;
    if (port.id === 'home') {
      v.mode = 'home'; v.islandId = null; state.boatMoored = true; stopWalk(); onHome();
      flash('已回到家园码头 · 可交易、补给或休息');
    } else {
      v.mode = 'ashore'; v.islandId = port.id;
      const firstVisit = !v.visited.includes(port.id);
      if (!v.discovered.includes(port.id)) v.discovered.push(port.id);
      if (firstVisit) v.visited.push(port.id);
      const land = landingPoint(port, true); v.walkX = land.x; v.walkZ = land.z;
      getAvatar().position.set(land.x, terrainHeight(port, land.x, land.z) + .02, land.z);
      flash(firstVisit ? `首次踏勘${port.name} · 调查纹章地标，寻找航标印记`
        : `抵达${port.name} · 点地面步行，走近地标调查`);
      if (firstVisit) onDiscovery({ title: port.name, detail: '首次踏勘 · 你的足迹已留在海图上' });
    }
    save(state); paintAt = -1;
  }
  function command(id) {
    if (activeVault && !['map', 'close', 'blank', 'track', 'supplies', 'stop'].includes(id) && !id.startsWith('select:')) {
      flash('请等翻越落地后再操作'); return;
    }
    if (isBlocked() && !['map', 'close', 'blank', 'track'].includes(id) && !id.startsWith('select:')) { flash('请先完成当前动作；休息时可查看海图'); return; }
    if (id === 'map') openMap();
    else if (id === 'close') mapOpen = false;
    else if (id === 'supplies') suppliesOpen = !suppliesOpen;
    else if (id.startsWith('select:')) selectedId = id.slice(7);
    else if (id === 'track') {
      v.trackedId = v.trackedId === selectedId ? null : selectedId;
      save(state); mapOpen = false;
      flash(v.trackedId ? `已标记${islandNotes(state, PORTS.find(i => i.id === selectedId)).name} · 方向提示会指向登陆点`
        : '已清除标记 · 继续自由探索');
    }
    else if (id === 'sail') {
      if (v.mode === 'home') {
        mapOpen = false; pendingStartDestination = selectedId;
        if (!boardFromHome()) pendingStartDestination = null;
      }
      else if (v.mode === 'ashore') walkBackToBoat(selectedId);
      else setDestination(selectedId);
    } else if (id === 'dock') dock();
    else if (id === 'stop') { route = []; destinationId = null; keys.clear(); hold = null; stopWalk(); }
    else if (id === 'home') { if (v.mode === 'ashore') walkBackToBoat('home'); else if (v.mode === 'sailing') setDestination('home'); }
    else if (id === 'drink' || id === 'meal') useSupply(id);
    else if (id === 'rescue') {
      if (v.mode === 'home') return;
      // Explicit recovery only, never triggered by fatigue or ordinary navigation.
      state.gold = Math.max(0, state.gold - 20); v.mode = 'home'; v.islandId = null; v.x = BOAT_MOOR.x; v.z = BOAT_MOOR.z; v.speed = 0;
      route = []; destinationId = null; stopWalk(); state.boatMoored = true; onHome(); save(state);
      flash('救援船已送回家园 · 支付最多 20 金，物品保留，船需维修');
    }
    paintAt = -1; paint();
  }
  function paint() {
    const screen = getScreen(), layout = voyageLayout(screen, getHudBounds?.(), v.mode);
    const { compact, landscape, chartSize, helmSize, controlHeight } = layout;
    chartRect = layout.chart; helmRect = layout.helm;
    resizeTextureCanvas(chartCanvas, chartTexture, chartSize.width, chartSize.height);
    resizeTextureCanvas(helmCanvas, helmTexture, helmSize.width, helmSize.height);
    chart.scale.set(chartRect.width, chartRect.height, 1); chart.position.set(chartRect.x, screen.height - chartRect.y, .1); chart.visible = mapOpen;
    helm.scale.set(helmRect.width, helmRect.height, 1); helm.position.set(helmRect.x, screen.height - helmRect.y, .1); helm.visible = v.mode !== 'home' && !mapOpen && !isHudOpen();
    const ctx = chartCanvas.getContext('2d'); ctx.clearRect(0, 0, chartSize.width, chartSize.height); ctx.fillStyle = 'rgba(6,37,46,.97)'; ctx.fillRect(0, 0, chartSize.width, chartSize.height);
    ctx.strokeStyle = '#ceb37c'; ctx.lineWidth = 3; ctx.strokeRect(3, 3, chartSize.width - 6, chartSize.height - 6);
    const quest = questSummary(state);
    ctx.fillStyle = '#fff1cb'; ctx.font = `700 ${compact ? 32 : 28}px sans-serif`; ctx.fillText('风帆群岛 · 寻回航标', 24, 40);
    ctx.font = `${compact ? 26 : 20}px sans-serif`; ctx.fillStyle = '#a9cdc7'; ctx.fillText(`${quest.lit ? '航标已点亮' : `航标印记 ${quest.seals}/3`} · 发现 ${v.discovered.length - 1}/4 · 登陆 ${v.visited.length - 1}/4`, 24, 69);
    const mx = 300, my = 273, scale = 1.62;
    const mapPoint = (p) => ({ x: mx + p.x * scale, y: my + p.z * scale });
    const mapPosition = v.mode === 'ashore' ? getAvatar().position : v;
    ctx.fillStyle = '#134957'; ctx.fillRect(22, 89, 556, 346);
    ctx.strokeStyle = '#2d6470'; ctx.lineWidth = 1;
    for (let x = 30; x < 578; x += 40) { ctx.beginPath(); ctx.moveTo(x, 89); ctx.lineTo(x, 435); ctx.stroke(); }
    for (let y = 100; y < 435; y += 40) { ctx.beginPath(); ctx.moveTo(22, y); ctx.lineTo(578, y); ctx.stroke(); }
    ctx.fillStyle = '#98b9b7'; ctx.font = '16px sans-serif'; ctx.fillText('N ↑', 535, 112); ctx.fillText('点击岛屿 · 标记方向或规划航线', 34, 420);
    chartButtons = [];
    for (const island of PORTS) {
      const p = mapPoint(island), known = v.discovered.includes(island.id);
      ctx.fillStyle = known ? island.color : '#577c7b'; ctx.beginPath(); ctx.ellipse(p.x, p.y, island.radius * scale, island.radius * scale * .85, 0, 0, Math.PI * 2); ctx.fill();
      if (state.beaconQuest.seals.includes(island.id) || island.id === 'beacon' && quest.lit) { ctx.fillStyle = '#fff0ac'; ctx.font = '700 20px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('✦', p.x, p.y + 6); }
      else if (v.visited.includes(island.id)) { ctx.fillStyle = '#163e39'; ctx.font = '700 20px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('✓', p.x, p.y + 6); }
      if (v.trackedId === island.id) { ctx.fillStyle = '#ffe18e'; ctx.font = '24px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('◆', p.x, p.y - 26); }
      if (selectedId === island.id) { ctx.strokeStyle = '#ffde89'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, 24, 0, Math.PI * 2); ctx.stroke(); }
      ctx.fillStyle = '#fff0d2'; ctx.font = '18px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(known ? island.name : '未知岛屿', p.x, p.y + 35);
      const hit = compact ? Math.max(78, Math.ceil(44 * chartSize.width / chartRect.width)) : 78;
      chartButtons.push({ id: `select:${island.id}`, x: p.x - hit / 2, y: p.y - hit / 2, w: hit, h: Math.max(82, hit) });
    }
    ctx.textAlign = 'start';
    if (route.length) {
      const current = mapPoint(v); ctx.strokeStyle = '#f8db85'; ctx.setLineDash([5, 5]); ctx.beginPath(); ctx.moveTo(current.x, current.y);
      route.forEach(p => { const point = mapPoint(p); ctx.lineTo(point.x, point.y); }); ctx.stroke(); ctx.setLineDash([]);
    }
    const p = mapPoint(mapPosition); ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(Math.PI - (v.mode === 'ashore' ? getAvatar().rotation.y : v.heading));
    ctx.fillStyle = '#fff0bb'; ctx.beginPath(); ctx.moveTo(0, -10); ctx.lineTo(-6, 7); ctx.lineTo(6, 7); ctx.closePath(); ctx.fill(); ctx.restore();
    const selected = PORTS.find(i => i.id === selectedId);
    const notes = islandNotes(state, selected);
    const infoX = landscape ? 610 : 26, infoWidth = landscape ? 408 : 550;
    const wrapped = (text, y, color, lines = 1) => {
      ctx.fillStyle = color;
      let line = '', row = 0;
      for (const character of text) {
        if (line && ctx.measureText(line + character).width > infoWidth && row < lines - 1) {
          ctx.fillText(line, infoX, y + row * 32, infoWidth); line = ''; row++;
        }
        line += character;
      }
      ctx.fillText(line, infoX, y + row * 32, infoWidth);
    };
    ctx.fillStyle = '#ffe5a6'; ctx.font = `700 ${compact ? 30 : 25}px sans-serif`; ctx.fillText(notes.name, infoX, landscape ? 106 : 466);
    ctx.font = `${compact ? 28 : 20}px sans-serif`;
    wrapped(notes.detail, landscape ? 148 : 500, '#c2d9d1', landscape ? 2 : 1);
    wrapped(notes.status, landscape ? 225 : 534, '#9fd3aa', landscape ? 2 : 1);
    const port = landingPoint(selected); ctx.fillStyle = '#c2d9d1'; ctx.fillText(`距登陆点 ${Math.round(Math.hypot(port.x - mapPosition.x, port.z - mapPosition.z))} 米`, infoX, landscape ? 291 : 562);
    function button(context, list, id, text, x, y, w, h = 56, color = '#287870') {
      context.fillStyle = color; context.fillRect(x, y, w, h); context.fillStyle = '#fff3d3'; context.font = `700 ${compact ? landscape ? 26 : 34 : 22}px sans-serif`; context.textAlign = 'center'; context.fillText(text, x + w / 2, y + h / 2 + 10, w - 8); context.textAlign = 'start'; list.push({ id, x, y, w, h });
    }
    button(ctx, chartButtons, 'close', '×', chartSize.width - (compact ? 112 : 74), compact ? 0 : 16, compact ? 88 : 48, compact ? 88 : 48, '#99703c');
    button(ctx, chartButtons, 'track', v.trackedId === selectedId ? '清除标记' : '标记方向', landscape ? 610 : 24, landscape ? 312 : 582, landscape ? 196 : 190, compact ? landscape ? 64 : 88 : 60, '#287870');
    button(ctx, chartButtons, 'sail', v.mode === 'home' ? '登船并前往' : '沿航线前往', landscape ? 822 : 230, landscape ? 312 : 582, landscape ? 196 : 346, compact ? landscape ? 64 : 88 : 60, '#a37b3a');
    const supplies = voyageSupplies(state);
    ctx.font = `${compact ? landscape ? 22 : 26 : 17}px sans-serif`; wrapped(supplies.summary, landscape ? 414 : compact ? 700 : 665, '#c2d9d1');
    wrapped(quest.lit ? '航标夜光已亮 · 继续自由探索群岛' : '调查顺序自由 · 点亮灯塔获 80 金', landscape ? 452 : compact ? 728 : 697, '#dbc28d');
    const h = helmCanvas.getContext('2d'); h.clearRect(0, 0, helmSize.width, helmSize.height); h.fillStyle = 'rgba(6,37,46,.95)'; h.fillRect(0, 0, helmSize.width, helmSize.height); h.strokeStyle = '#9caa85'; h.strokeRect(2, 2, helmSize.width - 4, helmSize.height - 4);
    h.fillStyle = '#fff0cf'; h.font = `700 ${compact ? landscape ? 28 : 34 : 26}px sans-serif`;
    const island = ISLANDS.find(i => i.id === v.islandId);
    h.fillText(v.mode === 'ashore' ? `${island.name} · 徒步探索` : `${destinationId ? `航向 ${PORTS.find(i => i.id === destinationId).name}` : '自由航行'} · ${v.speed.toFixed(1)} m/s`, 20, 36);
    h.fillStyle = '#bbd5ce'; h.font = `${compact ? 26 : 19}px sans-serif`;
    h.fillText(`船体 ${Math.round(state.boatDurability)}/100 · 水壶 ${state.freshwater.canteen}L · 累计 ${Math.round(v.distance)} 米`, 20, 66);
    if (!compact) h.fillText(v.mode === 'ashore' ? 'WASD 行走 · 点地面步行 · 走近地标按 E' : 'W / ↑ 前进 · A D 转向 · S / ↓ 减速 · 点海面航行', 20, 96);
    helmButtons = [];
    const firstY = compact ? landscape ? 70 : 92 : 118;
    const secondY = compact ? landscape ? v.mode === 'ashore' ? 82 : 144 : v.mode === 'ashore' ? 92 : firstY + controlHeight + 12 : 202;
    const thirdY = compact ? secondY + controlHeight + 12 : 276;
    const actionHeight = compact ? controlHeight : 56, supplyHeight = compact ? controlHeight : 38;
    const columns = landscape ? [20, 275, 530, 785] : [20, 195, 370, 545];
    const widths = landscape ? 230 : 155;
    if (v.mode === 'sailing') {
      button(h, helmButtons, 'left', '↶ 左转', columns[0], firstY, widths, controlHeight);
      button(h, helmButtons, 'forward', '↑ 前进', columns[1], firstY, widths, controlHeight);
      button(h, helmButtons, 'right', '右转 ↷', columns[2], firstY, widths, controlHeight);
      button(h, helmButtons, 'brake', '↓ 减速', columns[3], firstY, widths, controlHeight, '#695b52');
    } else if (v.mode === 'ashore' && !compact) h.fillText(islandNotes(state, island).detail, 20, 157, 676);
    if (landscape && suppliesOpen) {
      [['drink', '喝水'], ['meal', '吃饭'], ['stop', '停止'], ['rescue', '救援≤20金'], ['supplies', '返回']].forEach(([id, text], i) =>
        button(h, helmButtons, id, text, 20 + i * 204, secondY, 188, actionHeight));
    } else {
      button(h, helmButtons, 'map', '海图', columns[0], secondY, widths, actionHeight);
      button(h, helmButtons, 'dock', v.mode === 'ashore' ? '重新登船' : '靠岸下船', columns[1], secondY, widths, actionHeight, '#a37b3a');
      button(h, helmButtons, 'home', '航向家园', columns[2], secondY, widths, actionHeight);
      button(h, helmButtons, landscape ? 'supplies' : 'stop', landscape ? '更多' : '停止', columns[3], secondY, widths, actionHeight, '#695b52');
    }
    if (!landscape) {
      button(h, helmButtons, 'drink', '喝水', 20, thirdY, 145, supplyHeight, '#2b718b');
      button(h, helmButtons, 'meal', '吃饭', 185, thirdY, 145, supplyHeight, '#596b3e');
      button(h, helmButtons, 'rescue', '救援≤20金', 468, thirdY, 232, supplyHeight, '#76574a');
    }
    chartTexture.needsUpdate = true; helmTexture.needsUpdate = true;
  }
  const buttonAt = (x, y) => {
    const sprite = mapOpen ? chart : helm, rect = mapOpen ? chartRect : helmRect, buttons = mapOpen ? chartButtons : helmButtons;
    if (!sprite.visible || !rect || x < rect.x || x > rect.x + rect.width || y < rect.y || y > rect.y + rect.height) return null;
    return canvasButtonAt(rect, mapOpen ? chartCanvas : helmCanvas, buttons, x, y);
  };
  return {
    remoteGround,
    resize: paint,
    get engaged() { return v.mode !== 'home'; },
    get sailing() { return v.mode === 'sailing'; },
    get ashore() { return v.mode === 'ashore'; },
    get walking() { return v.mode === 'ashore' && (manualMoving || Boolean(walkTarget) || Boolean(activeVault)); },
    get mapOpen() { return mapOpen; },
    get controlsRect() { return helm.visible ? helmRect : null; },
    containsControls(x, y) { return mapOpen || Boolean(buttonAt(x, y)); },
    get focus() {
      if (v.mode === 'home') return null;
      if (v.mode === 'sailing') return v;
      const island = ISLANDS.find(i => i.id === v.islandId), avatar = getAvatar();
      if (!island || !avatar) return null;
      // Keep the landing beach and its landmarks in view while following walks.
      landFocus.x = island.x * .65 + avatar.position.x * .35;
      landFocus.z = island.z * .65 + avatar.position.z * .35;
      return landFocus;
    },
    get overviewFocus() {
      const island = ISLANDS.find(i => i.id === v.islandId);
      const center = v.mode === 'sailing' ? v : v.mode === 'ashore' && island ? island : HOME;
      overviewFocus.x = center.x; overviewFocus.z = center.z;
      overviewFocus.radius = v.mode === 'sailing' ? 18 : v.mode === 'ashore' && island ? island.radius : 14;
      return overviewFocus;
    },
    manualMove,
    stopManualMove,
    tryVault,
    get vaulting() { return Boolean(activeVault); },
    get vaultState() { return vaultFrame; },
    get colliders() { return islandColliders.get(v.islandId) || emptyColliders; },
    get walkPath() { return walkRoute; },
    open() { openMap(); paint(); },
    board,
    dismiss() { mapOpen = false; paintAt = -1; },
    resume() {
      stopWalk(); activeVault = null; vaultFrame = null;
      if (v.mode === 'ashore') {
        const island = ISLANDS.find(i => i.id === v.islandId);
        const point = restoreWalkPoint(island, { x: v.walkX, z: v.walkZ });
        if (!point) { flash('当前位置被实物围住，请选择附近可走的空地'); return; }
        v.walkX = point.x; v.walkZ = point.z;
        getAvatar().position.set(v.walkX, terrainHeight(island, v.walkX, v.walkZ) + .02, v.walkZ);
      }
      state.boatMoored = v.mode === 'home'; paint();
    },
    beginHold(x, y) {
      const b = buttonAt(x, y);
      if (!['forward', 'left', 'right', 'brake'].includes(b?.id)) return false;
      hold = b.id; route = []; destinationId = null; paintAt = -1; return true;
    },
    releaseControls() { hold = null; keys.clear(); stopManualMove(); },
    endHold() { const held = !!hold; hold = null; return held; },
    tap(x, y) { const b = buttonAt(x, y); if (!b) return mapOpen; if (!['forward', 'left', 'right', 'brake', 'blank'].includes(b.id)) command(b.id); return true; },
    key(event, down) {
      if (v.mode !== 'sailing' || !['w', 'a', 's', 'd', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key.toLowerCase().startsWith('arrow') ? event.key : event.key.toLowerCase())) return false;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      if (down) { keys.add(key); route = []; destinationId = null; } else keys.delete(key);
      event.preventDefault(); return true;
    },
    pick(action, point) {
      if (activeVault && v.mode === 'ashore') { flash('翻越完成后再选择去向'); return true; }
      if (action?.type === 'voyage-port') { if (v.mode === 'sailing') { const port = PORTS.find(i => i.id === action.islandId); selectedId = port.id; setDestination(port.id); } else flash('请先登船'); return true; }
      if (action?.type === 'voyage-gather') {
        if (v.mode !== 'ashore' || action.islandId !== v.islandId) { flash('先靠近浮标，减速后下船探索'); return true; }
        const object = nodes.get(`${action.islandId}-${action.node}`), target = object.getWorldPosition(new THREE.Vector3());
        queueWalk(target, action); return true;
      }
      if (action?.type === 'voyage-quest') {
        if (isBlocked()) { flash('请先完成当前动作'); return true; }
        if (v.mode !== 'ashore' || action.islandId !== v.islandId) { flash('先靠岸下船，再亲自调查地标'); return true; }
        const object = nodes.get(action.siteId);
        if (!object) return true;
        queueWalk(object.getWorldPosition(new THREE.Vector3()), action);
        return true;
      }
      if (v.mode === 'sailing') {
        if (point) { const next = planSeaRoute(v, point); if (next.length) { route = next; destinationId = null; flash('航向所选海面 · WASD 可接管'); } else flash('这里只能步行，船不能驶上沙滩'); }
        return true;
      }
      if (v.mode === 'ashore') {
        if (point) {
          const island = ISLANDS.find(i => i.id === v.islandId);
          queueWalk(point);
        }
        return true;
      }
      return false;
    },
    update(seconds, waterY) {
      age += seconds;
      const boat = getBoat(), avatar = getAvatar(); if (!boat || !avatar) return;
      if (v.mode === 'sailing') {
        const left = keys.has('a') || keys.has('ArrowLeft') || hold === 'left', right = keys.has('d') || keys.has('ArrowRight') || hold === 'right';
        let turn = Number(left) - Number(right), throttle = keys.has('w') || keys.has('ArrowUp') || hold === 'forward' ? 1 : 0;
        if (keys.has('s') || keys.has('ArrowDown') || hold === 'brake') throttle = 0;
        const maxSpeed = state.boatDurability <= 0 ? .65 : ({ wood: 5, iron: 6.5, speed: 9 })[state.boatTier] || 3;
        if (route.length) {
          const input = autopilotInput(v, route, maxSpeed);
          turn = input.turn; throttle = input.throttle;
          if (input.arrived && destinationId) flash('已到达浮标 · 减速后点击「靠岸下船」');
        }
        const result = stepHelm(v, seconds, { throttle, turn, maxSpeed });
        if (result.collision) {
          route = []; destinationId = null;
          if (age - collisionAt > 2) { state.boatDurability = Math.max(0, state.boatDurability - 2); flash('船触到浅滩或海域边界 · 转向驶离，船体 -2'); collisionAt = age; }
        }
        // Sitting at the helm: feet stay inside the hull, no walking/swimming pose.
        boat.position.set(v.x, waterY + .10 + Math.sin(age * 1.5) * .025, v.z);
        boat.rotation.set(Math.sin(age * 1.8) * .025, v.heading - Math.PI / 2, -turn * v.speed * .014);
        seatAvatarInBoat(avatar, boat);
        for (const island of ISLANDS) if (!v.discovered.includes(island.id) && Math.hypot(v.x - island.x, v.z - island.z) < island.radius + 17) {
          v.discovered.push(island.id); save(state); flash(`发现${island.name} · ${islandNotes(state, island).detail}`);
          onDiscovery({ title: island.name, detail: '新的岛影 · 靠近浮标后下船探索' });
        }
      } else if (v.mode === 'ashore') {
        resetAvatarSeatedJoints(avatar);
        boat.position.set(v.x, waterY + .1 + Math.sin(age * 1.5) * .025, v.z);
        boat.rotation.set(0, v.heading - Math.PI / 2, 0);
        const island = ISLANDS.find(i => i.id === v.islandId);
        let landed = false;
        if (activeVault) {
          vaultFrame = stepVault(activeVault, isBlocked() ? 0 : Math.min(.1, Math.max(0, seconds)));
          const pose = vaultFrame, lift = Math.sin(pose.progress * Math.PI);
          avatar.position.set(pose.x, terrainHeight(island, pose.x, pose.z) + pose.height + .02, pose.z);
          avatar.rotation.x = -.12 * lift; avatar.rotation.z = 0;
          avatar.userData.limbs.leftLeg.rotation.x = .42 * lift; avatar.userData.limbs.rightLeg.rotation.x = .32 * lift;
          avatar.userData.seatedRig?.knees.forEach(knee => { knee.rotation.x = .65 * lift; });
          avatar.userData.limbs.leftArm.rotation.x = -.4 * lift; avatar.userData.limbs.rightArm.rotation.x = -.4 * lift;
          if (pose.done) { activeVault = null; manualMoving = false; landed = true; }
        } else {
          if (manualMoving && !isBlocked()) {
            avatar.userData.limbs.leftLeg.rotation.x = Math.sin(age * 9) * .32;
            avatar.userData.limbs.rightLeg.rotation.x = -avatar.userData.limbs.leftLeg.rotation.x;
          } else if (walkTarget && !isBlocked()) {
            const waypoint = walkRoute[0], distance = waypoint ? Math.hypot(waypoint.x - avatar.position.x, waypoint.z - avatar.position.z) : 0;
            if (waypoint && distance > .025) {
              const step = Math.min(distance, Math.min(.1, Math.max(0, seconds)) * 3), dx = waypoint.x - avatar.position.x, dz = waypoint.z - avatar.position.z;
              const previous = { x: avatar.position.x, z: avatar.position.z }, desired = { x: previous.x + dx / distance * step, z: previous.z + dz / distance * step };
              const moved = resolveIslandStep(island, previous, desired);
              if (!shoreSafe(island, moved.x, moved.z) || Math.hypot(moved.x - desired.x, moved.z - desired.z) > .015) {
                stopWalk(); flash('前方被实物挡住，请重新选择路线');
              } else {
                avatar.position.x = moved.x; avatar.position.z = moved.z; avatar.rotation.y = Math.atan2(dx, dz);
                avatar.userData.limbs.leftLeg.rotation.x = Math.sin(age * 9) * .32; avatar.userData.limbs.rightLeg.rotation.x = -avatar.userData.limbs.leftLeg.rotation.x;
                if (distance <= step + .025) walkRoute.shift();
              }
            } else if (waypoint) walkRoute.shift();
            if (walkTarget && !walkRoute.length) {
              const safelyArrived = canStand(island, avatar.position.x, avatar.position.z) && Math.hypot(avatar.position.x - walkTarget.x, avatar.position.z - walkTarget.z) < .08;
              walkTarget = null;
              if (!safelyArrived) { pendingGather = null; pendingDeparture = undefined; }
              if (pendingGather) {
                v.walkX = avatar.position.x; v.walkZ = avatar.position.z;
                const isQuest = pendingGather.type === 'voyage-quest';
                const result = isQuest ? interactQuestSite(state, pendingGather.siteId) : harvestIsland(state, pendingGather.islandId, pendingGather.node);
                flash(result.message);
                if (!isQuest || result.changed) save(state);
                if (isQuest && result.completed) onDiscovery({ title: '航标重燃', detail: '群岛间的归途有了光 · 获得 80 金币' });
                else if (isQuest && result.changed && result.message.includes('印记')) onDiscovery({ title: `航标印记 ${questSummary(state).seals}/3`, detail: questSummary(state).text });
                pendingGather = null;
              }
              if (pendingDeparture !== undefined) {
                const nextDestination = pendingDeparture; pendingDeparture = undefined;
                const landing = landingPoint(island, true);
                if (Math.hypot(avatar.position.x - landing.x, avatar.position.z - landing.z) <= .3 && board() && nextDestination) setDestination(nextDestination);
              }
            }
          } else { avatar.userData.limbs.leftLeg.rotation.x = 0; avatar.userData.limbs.rightLeg.rotation.x = 0; }
          avatar.rotation.x = 0; avatar.rotation.z = 0; avatar.position.y = terrainHeight(island, avatar.position.x, avatar.position.z) + .02;
          avatar.userData.limbs.leftArm.rotation.x = -avatar.userData.limbs.leftLeg.rotation.x * .65;
          avatar.userData.limbs.rightArm.rotation.x = -avatar.userData.limbs.rightLeg.rotation.x * .65;
        }
        // Save the last grounded position while airborne, so an interrupted
        // reload never places the standing actor inside the vaulted prop.
        if (!activeVault) { v.walkX = avatar.position.x; v.walkZ = avatar.position.z; }
        if (landed) save(state);
      }
      const focus = v.mode === 'ashore' ? avatar.position : v.mode === 'sailing' ? v : { x: 0, z: 0 };
      islandArt.forEach(art => animateIslandArt(art, age, waterY, focus, seconds, state.clockHours, state));
      questArt.forEach(art => art.update(state, age, state.clockHours, focus));
      if (age - paintAt > .18) { paint(); paintAt = age; }
    },
    get wake() { return v.mode === 'sailing' ? Math.min(1, v.speed / 3) : 0; },
    get heading() { return v.heading; },
  };
}
